import React from "react"
import { StatusCardProps } from "features/bike/models/bikePage"
import { useTranslation } from "react-i18next"
import { privateRoutes } from "router/routes"
import { useNavigate } from "react-router-dom"
import { format } from "date-fns"

import * as BikeApi from "features/bike/api/BikeApi"
import { useAlert, useForm } from "hooks"

import { ErrorResponse } from "models"
import { Button } from "components/Elements/Button"
import { Modal, Spinner } from "components/Elements"
import { FormElement, formStatusEnum } from "components/Form"
import { BIKE_STATUS_DESTROY, isNotEmpty } from "utils"
import { useBikesStore } from "features/dashboard/hooks"

type defaultFormFieldsProps = {
	bikeCode: string
}
const defaultFormFields: defaultFormFieldsProps = {
	bikeCode: "",
}

export const DestroyStatusCard: React.FC<StatusCardProps> = ({ bike, isDisabled }) => {
	const navigate = useNavigate()
	const { fetchBikes } = useBikesStore()
	const {
		formStatus,
		setFormStatus,
		formFields,
		handleChange,
		showLocalErrors,
		handleValidation,
		setShowLocalErrors,
		errors,
	} = useForm({
		defaultFormStatus: formStatusEnum.ready,
		defaultFormFields: defaultFormFields,
	})
	const [isModalOpen, setModalIsOpen] = React.useState<boolean>(false)

	const isLoading = formStatus === formStatusEnum.loading
	const isReady = formStatus === formStatusEnum.ready

	const { t } = useTranslation()
	const { alert } = useAlert()

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		const formReady = !(errors.length > 0)
		if (!formReady) {
			return setShowLocalErrors(true)
		}
		try {
			setFormStatus(formStatusEnum.loading)

			const operatorData = {
				...bike.operator_data,
				destroy_date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
			}

			const updatedBikePayload = {
				bike_id: bike.bike_id,
				row_version: bike.row_version,
				bike_status: BIKE_STATUS_DESTROY,
				operator_data: operatorData,
			}

			const { getError } = await BikeApi.updateBike(updatedBikePayload)
			if (getError()) throw getError()

			alert("success", t("info_update", { ns: "form" }))
			navigate(privateRoutes.dashboard.path)
			fetchBikes() // Reload bikes
		} catch (error) {
			console.error(error)
			const errorMsg = (error as ErrorResponse)?.message?.toString()
			alert("error", errorMsg)
			setFormStatus(formStatusEnum.ready)
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<>
			<div className="w-full lg:w-1/2 xl:w-1/4">
				<div className={`c-status-card p-5 mx-3 mb-6 ${isDisabled ? "is-disabled" : ""}`}>
					<p className="text-m font-bold mb-6">
						{t("bike_state.destroy_status", {
							ns: "bike",
						})}
					</p>
					{isReady && (
						<Button
							variant="primary"
							type="submit"
							onClick={() => setModalIsOpen((prevIsModalOpen) => !prevIsModalOpen)}
							className="px-2 py-1"
							size="small"
							disabled={isDisabled}
						>
							{t("bike_state.destroy_button", {
								ns: "bike",
							})}
						</Button>
					)}

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}
				</div>
			</div>

			<Modal
				title={t("bike_state.destroy_modal_title", {
					ns: "bike",
				})}
				isOpen={isModalOpen}
				setIsOpen={setModalIsOpen}
				className={"max-w-2xl"}
			>
				<p className="text-center md:px-6 mt-8">
					{t("bike_state.destroy_modal_description_p1", {
						ns: "bike",
					})}
				</p>
				<p className="text-center md:px-6 mt-8 font-bold">
					{t("bike_state.destroy_modal_description_p2", {
						ns: "bike",
					})}
				</p>
				<p className="text-center md:px-6 mb-8">
					{t("bike_state.destroy_modal_description_p3", {
						ns: "bike",
					})}
				</p>
				<form data-cy="destroy-bike">
					<div className="md:px-6 mb-8 flex justify-center">
						<FormElement
							{...formElementCommonProps}
							value={formFields.bikeCode}
							className="w-[100%] max-w-[480px] block"
							type="text"
							name="bikeCode"
							id="bikeCode"
							required={true}
							customRules={[
								{
									key: "codeBike-good",
									check: (value: string) =>
										isNotEmpty(value) ? value === bike.bike_id : true,
									message: t("bike_state.destroy_codeBike_not_good", {
										ns: "bike",
									}),
								},
							]}
						/>
					</div>

					<div className="flex justify-center">
						<Button
							onClick={handleSubmit}
							type="submit"
							className="mx-2 md:px-20 max-md:px-8 py-1"
							variant="secondary"
						>
							{t("bike_state.destroy_modal_confirm", {
								ns: "bike",
							})}
						</Button>
						<Button
							type="button"
							onClick={() => setModalIsOpen(false)}
							className="mx-2 px-8 py-1 min-w-0"
							variant="secondaryBordered"
						>
							{t("global.cancel")}
						</Button>
					</div>
				</form>
			</Modal>
		</>
	)
}
